/* KC design system - behaviour layer (site-wide).
   Dependency-free. Progressive enhancement only: every feature below
   degrades gracefully when JavaScript is unavailable.

   This is the entry module: it owns nothing but the wiring. The features
   live in ./ds/*.js and are started here, in an order that matters — see
   the notes on the sequence below. */
import { renderMermaid, renderMath, headingAnchors, frameCodeBlocks, wrapTables, normaliseEmbeds, externalLinks } from './ds.min/prose.js?v=3fe3e547';
import { navToggle, headerScroll, railLatch } from './ds.min/nav.js?v=42814e2c';
import { drawers, modals } from './ds.min/overlays.js?v=461cabff';
import { deckStages } from './ds.min/deck.js?v=aa0dd9ae';
import { deckStudio } from './ds.min/deck-studio.js?v=e7332d4f';
import { screenStudio } from './ds.min/screen-studio.js?v=cdfff30f';
import { toasts } from './ds.min/toast.js?v=3de76c41';
import { sectionNav } from './ds.min/section-nav.js?v=3dd1c1c5';
import { progressStrips } from './ds.min/progress.js?v=bcdd6cf6';
import { revealOnScroll, splitLetters } from './ds.min/reveal.js?v=160d1b95';
import { carousels, projectFilters, expandableCards } from './ds.min/cards.js?v=246710aa';
import { pageLoader, loaderDemos, customCursor } from './ds.min/page-fx.js?v=220c8ad8';
import { skillBars, statCounters, initHomeMotion } from './ds.min/counters.js?v=94582d21';
import { articleExtras, yearMarkers } from './ds.min/article.js?v=e423a92e';
import { recordSeriesRead, seriesNextUp, seriesProgress, continueReading } from './ds.min/series.js?v=6767688a';
import { loadMore } from './ds.min/load-more.js?v=ba485f93';
import { themePicker } from './ds.min/theme.js?v=a80e93f5';
import { tabs, postView } from './ds.min/tabs.js?v=b7c0d1d5';
import { accordion } from './ds.min/accordion.js?v=8a235e8b';
import { specimenSource, specimenCounts } from './ds.min/specimen.js?v=b685b64a';
import { sessionApps } from './ds.min/session.js?v=5483a24e';
import { controlSwitches } from './ds.min/control-switches.js?v=5e603e59';

/* The `js` class that gates JS-only styles is not set here: a module is
   deferred by definition, so it lands after first paint and anything hidden
   by that gate would flash visible and then snap away. _Styles.cshtml sets it
   inline in <head> instead, before any stylesheet is parsed. */

document.addEventListener('DOMContentLoaded', function () {
    renderMermaid(document);
    renderMath(document);
    headingAnchors(document);
    frameCodeBlocks(document);
    wrapTables(document);
    normaliseEmbeds(document);
    externalLinks(document);
    navToggle();
    /* After the rail exists and before anything measures it. A no-op on every
       page whose rail carries no hidden mount, which is every page on a site
       with the fourth app switched off. */
    sessionApps(document);
    /* no-op unless the page wears the rail; the collapsed state itself is
       already on the element, set inline before first paint */
    railLatch();
    /* drawers() first: sectionNav() binds to the ds-drawer-open/close events
       the drawer fires, and only a drawer that has been wired fires them */
    drawers();
    modals();
    /* the third overlay, and the only one with nothing on the page to wire:
       it publishes window.dsToast and listens for [data-ds-toast] triggers */
    toasts();
    sectionNav();
    /* no-op unless the page (or a showcase frame) carries a progress strip */
    progressStrips();
    /* accordion() before the demo modules below: arriving at
       /design-system/blog#series-map has to open that section and
       shut the one the server opened BEFORE anything measures a box, and the
       ds-content-added it fires on the opened body is what the modules below
       answer. sectionNav() above needs nothing from it -- it imports
       revealTarget directly, which works whether or not this has run. */
    accordion();
    /* delegated on document, so it needs no re-run after a group swap -- it is
       here rather than in the handler below for exactly that reason */
    specimenSource();
    /* ...and its opposite: the counts ARE per-panel, so this one IS re-run in
       the handler below. It reads the accordion heads, which is why it does
       not wait for a body to open -- a closed <details> still renders its
       content, so the cells are there to count. */
    specimenCounts(document);
    splitLetters();
    /* carousels() first: projectFilters() drives them through
       ds-carousel-refresh, which nothing is listening for until then */
    carousels();
    projectFilters();
    expandableCards();
    headerScroll();
    pageLoader();
    /* no-op everywhere but the showcase, which is the only page carrying a
       framed loader specimen */
    loaderDemos();
    customCursor();
    loadMore();
    /* no-op everywhere but the showcase, which is where the picker lives */
    themePicker();
    /* postView() first: it re-marks the post-view strip to the class the
       before-paint script set, and tabs() reads that selection for the
       roving tabindex */
    postView();
    tabs();
    yearMarkers();
    articleExtras();
    /* recordSeriesRead() first: it writes this post into the read set that
       seriesNextUp() reads back to decide which row is next */
    recordSeriesRead();
    seriesNextUp();
    seriesProgress();
    continueReading();

    /* the showcase renders a real deck inside a stage; that is a component on
       an ordinary page, not the page becoming a deck */
    deckStages();
    /* no-op everywhere but /design-system/studio, which is the only page that
       carries the picker. It used to be a section of the showcase, which meant
       it could also arrive in a fetched group fragment -- hence a second boot
       from the ds-content-added handler below. It is a page now, and a page
       never has its body swapped, so this is the only boot it gets. */
    deckStudio();
    /* no-op everywhere but /design-system/screens, the only page carrying the
       screen studio; a page is never body-swapped, so this is its one boot */
    screenStudio();
    /* no-op everywhere but /control/switches, which is the only page that
       carries the console. Started here rather than from a page-scoped script
       tag for the reason the pipeline's own notes give: a static import
       specifier is a fixed string, so only a module in this graph gets its
       ?v= hash rewritten -- a page that linked ds.min/ directly would be
       served a thirty-day-stale copy after the next edit. */
    controlSwitches();

    var pageDeck = Array.prototype.filter.call(
        document.querySelectorAll('.ds-deck'),
        function (el) { return !el.closest('.ds-deck-stage'); })[0];
    if (pageDeck) {
        initHomeMotion();
    } else {
        // ordinary pages (blog, design system): plain reveal-on-scroll
        revealOnScroll();
        skillBars();
        statCounters();
    }

    /* last: everything above may have moved code blocks into their frames,
       and Prism highlights whatever it finds at the moment it runs.

       highlightAllUnder(document.body), not highlightAll(): the two are the
       same work here, but naming the root makes this call the same shape as
       the scoped one in the ds-content-added handler below, and the pair is
       what stops the next person restoring the unscoped version there. */
    if (window.Prism && typeof window.Prism.highlightAllUnder === 'function') {
        window.Prism.highlightAllUnder(document.body);
    }
});

/* The showcase's group tabs swap the sections in place (tabs.js fetches the
   group as a fragment and replaces the [data-ds-tab-host] subtree), and the
   arriving HTML is full of live demos that were wired by the pass above --
   none of which exist until this event says so. The prose helpers take the
   new subtree as their root; the rest are re-runnable because they skip
   elements an earlier pass already claimed. Delegated features (overlays,
   toasts, expandable cards) need nothing: their listeners live on document
   and survive any swap. progress.js and section-nav.js keep their own
   ds-content-added listeners, because their re-init is a re-measure of
   state they closed over, not a re-wire. */
document.addEventListener('ds-content-added', function (e) {
    var root = e.target;
    /* a swapped-in group may carry the deck-chrome section, which renders a
       real deck inside a stage. It can no longer carry the STUDIO: that left
       the catalog for /design-system/studio, so deckStudio() is booted once on
       load and has nothing to re-wire here. */
    deckStages(root);
    /* Overlays ARE in this block, contrary to the note above, and only for
       the semantics: a modal that arrives in a swapped group has the
       delegated listeners already but none of role, aria-modal, aria-hidden
       or tabindex, which are per element. modals() is idempotent and skips
       any panel an earlier pass claimed. */
    modals();
    renderMermaid(root);
    renderMath(root);
    headingAnchors(root);
    frameCodeBlocks(root);
    wrapTables(root);
    normaliseEmbeds(root);
    externalLinks(root);
    progressStrips();
    splitLetters();
    carousels();
    projectFilters();
    loaderDemos();
    skillBars();
    statCounters();
    tabs();
    /* the swapped panel brings twelve unwired disclosures; the surviving
       chrome brings none. accordion() is re-runnable for exactly that reason
       and its address handling is deliberately not (the URL did not change
       because a group swapped -- tabs.js owns that entry). */
    accordion(root);
    /* the swapped panel brings twelve unnumbered heads */
    specimenCounts(root);
    revealOnScroll(root);
    /* Scoped, like every other call in this handler. highlightAll() re-tokenises
       every code block in the document, and it re-tokenises them from the
       already-highlighted markup -- so the cost is the whole page's code, paid
       again, every time a disclosure opens for the first time. With one code
       block per section that was invisible; the publishing channel would put one
       under every demo group, and then it is not. */
    if (window.Prism && typeof window.Prism.highlightAllUnder === 'function') {
        window.Prism.highlightAllUnder(root);
    }
});
